import type { FC } from 'react';

import type { BankConnection as BankConnectionType } from '@api-client/address-book';
import { getFeatureConfig } from '@luna/feature-config';

import { Iban } from '../iban/Iban';
import { mapBankConnectionToAccount } from '../bankConnection/BankConnectionService';
import { formatPhoneNumber } from '../bank-connection-with-secondary-id/BankConnectionWithSecondaryIdService';
import {
    getCZFormattedAccount,
    getFormattedAccount,
    getHUFormattedAccount,
    getRSFormattedAccount,
} from '../account-no/AccountNoService';
import { TextWithOptionalHighlight } from './TextWithOptionalHighlight';

interface Props {
    bankConnection: BankConnectionType;
    highlightedText?: string;
}

export const BankConnectionWithTextHighlighter: FC<Props> = ({
    bankConnection,
    highlightedText,
}) => {
    const highlightedTextWithoutSpaces = highlightedText ? highlightedText.replace(/\s+/g, '') : '';
    const { secondaryId, ...restBankConnection } = bankConnection;

    if (secondaryId?.type === 'PHONE') {
        return (
            <TextWithOptionalHighlight
                text={formatPhoneNumber(secondaryId.id)}
                highlightedText={highlightedTextWithoutSpaces}
            />
        );
    }

    const account = mapBankConnectionToAccount(restBankConnection);
    const { country } = getFeatureConfig(({ base }) => base);

    if (country === 'CZ') {
        const { number, iban } = getCZFormattedAccount(account);

        return [
            number && (
                <div>
                    <TextWithOptionalHighlight
                        text={number}
                        highlightedText={highlightedTextWithoutSpaces}
                        dataCy="account-number-CZ"
                    />
                </div>
            ),
            iban && (
                <TextWithOptionalHighlight
                    text={Iban.formatIban(iban)}
                    highlightedText={highlightedTextWithoutSpaces}
                    dataCy="iban-default"
                />
            ),
        ];
    }

    if (country === 'HU') {
        const { number, iban } = getHUFormattedAccount(account);

        return [
            number && (
                <div>
                    <TextWithOptionalHighlight
                        text={number}
                        highlightedText={highlightedTextWithoutSpaces}
                        dataCy="account-number-HU"
                    />
                </div>
            ),
            iban && (
                <TextWithOptionalHighlight
                    text={Iban.formatIban(iban)}
                    highlightedText={highlightedTextWithoutSpaces}
                    dataCy="iban-default"
                />
            ),
        ];
    }

    if (country === 'RS') {
        const { number, iban } = getRSFormattedAccount(account);

        return [
            number && (
                <TextWithOptionalHighlight
                    text={number}
                    highlightedText={highlightedTextWithoutSpaces}
                    dataCy="account-number-RS"
                />
            ),
            iban && (
                <TextWithOptionalHighlight
                    text={Iban.formatIban(iban)}
                    highlightedText={highlightedTextWithoutSpaces}
                    dataCy="iban-default"
                />
            ),
        ];
    }

    const { iban, number } = getFormattedAccount(account);

    if (iban) {
        const dataCy = country === 'SK' ? 'iban-default' : 'iban-sk';
        return (
            <TextWithOptionalHighlight
                text={Iban.formatIban(iban)}
                highlightedText={highlightedTextWithoutSpaces}
                dataCy={dataCy}
            />
        );
    }

    if (number) {
        return (
            <TextWithOptionalHighlight
                text={number}
                highlightedText={highlightedTextWithoutSpaces}
                dataCy="account-number-default"
            />
        );
    }

    return null;
};
